<div class="space-y-5">
  <!-- Invoices Summary Card -->
  <div class="p-3.5 bg-surface-high border border-border rounded-xl space-y-2">
    @if (invoices.length === 1) {
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <lucide-icon name="file-text" class="w-4 h-4 text-muted"></lucide-icon>
          <app-text size="sm" class="font-bold text-foreground">Invoice #{{ invoices[0].invoiceNumber }}</app-text>
        </div>
        <app-badge [title]="invoices[0].status" variant="secondary" badgeStyle="soft"></app-badge>
      </div>
      <div class="text-xs text-muted space-y-1">
        <div class="flex justify-between">
          <span>Billed To:</span>
          <span class="font-medium text-foreground">{{ invoices[0].billedToName || '—' }}</span>
        </div>
        <div class="flex justify-between">
          <span>Total Amount:</span>
          <span class="font-medium text-foreground">{{ invoices[0].totalAmount | currency }}</span>
        </div>
        <div class="flex justify-between">
          <span>Associated Loads:</span>
          <span class="font-medium text-foreground">{{ invoices[0].loadIds?.length || 0 }} load(s)</span>
        </div>
      </div>
    } @else {
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <lucide-icon name="files" class="w-4 h-4 text-muted"></lucide-icon>
          <app-text size="sm" class="font-bold text-foreground">{{ invoices.length }} Invoices Selected</app-text>
        </div>
        <span class="font-bold text-foreground">{{ totalGrossAmount() | currency }}</span>
      </div>
      <div class="text-xs text-muted">
        <span>Total loads across selected invoices: <strong>{{ totalLoadCount() }}</strong></span>
      </div>
    }
  </div>

  <!-- Warning Banner -->
  <div class="p-3.5 bg-surface border border-danger rounded-xl flex items-start gap-3">
    <lucide-icon name="alert-triangle" class="w-5 h-5 text-danger shrink-0 mt-0.5"></lucide-icon>
    <div class="text-xs space-y-1">
      <div class="font-medium text-danger">
        Permanent Voiding (Soft-Delete)
      </div>
      <div class="text-muted">
        This invoice will be marked as <strong>VOIDED</strong>. Please select how the {{ totalLoadCount() }} associated load{{ totalLoadCount() === 1 ? '' : 's' }} should be routed.
      </div>
    </div>
  </div>

  <!-- Load Routing Selection -->
  <div class="space-y-2">
    <app-text size="xs" weight="semibold" class="uppercase tracking-wider text-muted block">
      Associated Loads Action
    </app-text>

    <div class="space-y-2">
      <label
        class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
        [class]="returnToReady() ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-surface-high'"
        (click)="returnToReady.set(true)"
      >
        <input
          type="radio"
          name="voidRouting"
          [checked]="returnToReady()"
          class="mt-1"
        />
        <div>
          <app-text size="sm" weight="semibold" class="text-foreground block">
            Return all loads to Ready to Invoice
          </app-text>
          <app-text size="xs" variant="muted" class="block">
            Restores all loads to the unbilled queue so they can be re-invoiced with required changes or added accessorials.
          </app-text>
        </div>
      </label>

      <label
        class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
        [class]="!returnToReady() ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-surface-high'"
        (click)="returnToReady.set(false)"
      >
        <input
          type="radio"
          name="voidRouting"
          [checked]="!returnToReady()"
          class="mt-1"
        />
        <div>
          <app-text size="sm" weight="semibold" class="text-foreground block">
            Mark all loads as Do Not Invoice
          </app-text>
          <app-text size="xs" variant="muted" class="block">
            Excludes all loads from ever being billed (sets billing status to N/A).
          </app-text>
        </div>
      </label>
    </div>
  </div>

  <!-- Reason Form -->
  <form [formGroup]="form" (ngSubmit)="confirmVoid()" class="space-y-4">
    <app-form-field
      label="Reason for Voiding"
      hint="Required explanation for accounting and audit compliance"
      [required]="true"
    >
      <app-input
        formControlName="reason"
        placeholder="e.g. Rate discrepancy with broker / Re-invoicing with added detention / Wrong factoring company"
        size="md"
      ></app-input>
    </app-form-field>

    <!-- Email Notification Checkbox -->
    @if (invoices[0]?.billedToEmail || invoices[0]?.recipientEmail) {
      <div class="p-3 bg-surface rounded-lg border border-border flex items-start gap-2.5">
        <app-checkbox
          [ngModel]="sendVoidEmail()"
          (ngModelChange)="sendVoidEmail.set($event)"
          class="mt-0.5"
        ></app-checkbox>
        <div class="text-xs">
          <span class="font-medium text-foreground block">Send Void Notice email to recipient</span>
          <span class="text-muted block mt-0.5">
            Will reply directly to the email thread with a high-priority cancellation notification instructing them not to process the invoice.
          </span>
        </div>
      </div>
    }

    <!-- Actions -->
    <div class="flex items-center justify-end gap-3 pt-3 border-t border-border">
      <app-button
        type="button"
        variant="ghost"
        (click)="close()"
        [disabled]="isSaving()"
      >
        Cancel
      </app-button>
      <app-button
        type="submit"
        variant="danger"
        [loading]="isSaving()"
        [disabled]="form.invalid || !canEditBilling() || isSaving()"
        icon="ban"
      >
        Void Invoice{{ invoices.length > 1 ? 's' : '' }}
      </app-button>
    </div>
  </form>
</div>
